import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import TombolaBaseComponent from '@/components/base/base.component';
import { ChatMessageModel, MessageType, ChatPrizeResponse, JackpotPrizeNotificationEventName, CMMessageColourDefaults, ArcadeWinnerMessageResponse, ArcadeWinnerNotificationEventName, ArcadeWinnerReceivedEventName } from '@/components/chat/_domain';
import { IChatServerService, ServerServiceSubscriptionStore, IAuthenticationResponse } from '@/app-modules/server-service';
import { MessageResponse, HistoryResponse, AddUserResponse, RemoveUserResponse, JackpotWinnerMessageResponse } from '@/components/chat/_domain';
import { __decodeString, __encodeString } from '@/helper/string-extensions/extensions';
import { CMBanSinPrizeEventName, ChatContextMenuModule, IChatContextMenuFactory } from '@/components/chat/_domain/modules/chat-context-menu';
import { ConfigurationKey, IGameConfigurationService, ConfigurationPaths, IConfigurationService, IChatModsConfiguration } from '@/app-modules/config';
import { OpenContextMenuEventName } from '@/app-modules/tombola-context-menu';
import { ChatBuffer } from '@/components/chat/_domain/modules/chat-buffer';
import { UserPreferences, ColourTheme } from '@/app-modules/store/modules/user-preferences';
import { IAlertWordsService } from '@/components/chat/_domain/modules/alert-words/alert-words-service';
import { User } from '@/app-modules/store/modules/user';
import { IUserChatStatusService, UserChatStatus } from '@/components/chat/_domain/modules/user-chat-status-service/user-chat-status-service-interface';
import { RoomUserContainer } from '@/components/chat/_domain/modules/room-user-container/room-user-container';
import { SERVICE_IDENTIFIERS } from '@/app-modules/_ioc/service-identifiers';
import { container } from '@/app-modules/_ioc';
import { invertColor, darkenColor, colorSimilarity, colorDifference, adjustColor } from '@/helper';
import { numberToLocalFormat } from '@/app-modules/utils/number-formatter';
import { GameLocalisation } from '@/components/game/_domain/modules/game-localisation';
import { fontTagExtractor } from '@/app-modules/utils';

@Component({
  name: 'MessageListComponent'
})
export default class MessageListComponent extends TombolaBaseComponent {
  @Prop() public modalGuid: string;
  @Prop() public server: IChatServerService;
  @Prop() public isChatFrozen: boolean;
  @Prop() public showEnterAndExitMessages: boolean;
  @Prop() public gameName: string;
  @Prop() public chatRoomName: string;
  @Prop() public chatBackgroundColour: string;
  @Prop() public roomUserContainer: RoomUserContainer;
  private chatMessageList: ChatMessageModel[] = [];
  private chatBuffer: ChatBuffer = null;
  private frozenChatBuffer: ChatBuffer = null;
  private chatContextMenu: ChatContextMenuModule = null;
  private serverSubscriptionStore: ServerServiceSubscriptionStore = null;
  private banSinPrizeEventHandler: (message: string) => void = null;
  private gameLocalisation: GameLocalisation = new GameLocalisation(this.$lang);

  private get messageListStyle() {
    let color = invertColor(darkenColor(this.chatBackgroundColour, '20%'));
    const similarity = colorSimilarity(this.chatBackgroundColour, color);
    const difference = colorDifference(this.chatBackgroundColour, color);
    if (similarity >= 0.8) {
      const adjustment = `${difference < 0 ? '' : '-'}${(similarity * 100).toFixed(2)}%`;
      color = adjustColor(this.chatBackgroundColour, adjustment);
    }
    return { color };
  }

  private isCmChat(): boolean {
    return this.gameName === 'cmchat';
  }

  private isCsChat(): boolean {
    return this.gameName === 'cschat';
  }

  private get userChatStatusService(): IUserChatStatusService {
    return this.$container.get(SERVICE_IDENTIFIERS.IUserChatStatusService);
  }

  private get alertWordService(): IAlertWordsService {
    return this.$container.get(SERVICE_IDENTIFIERS.IAlertWordsService) as IAlertWordsService;
  }

  private get configurationService(): IConfigurationService {
    return this.$container.get(SERVICE_IDENTIFIERS.IConfigurationService) as IConfigurationService;
  }

  private get gameConfigurationService(): IGameConfigurationService {
    return this.$container.get(SERVICE_IDENTIFIERS.IGameConfigurationService) as IGameConfigurationService;
  }

  private updated(): void {
    const scrollContainer = this.$refs.scrollContainer as any;
    if (scrollContainer && !this.isChatFrozen) {
      scrollContainer.scrollToBottom();
    }
  }

  private beforeDestroy(): void {
    if (this.isCmChat() || this.isCsChat()) {
      this.unsubscribeFromBanSinPrizeEvents();
    }
    this.unsubscribeFromMessageStreams();
    this.disposeChatBuffers();
  }

  @Watch('server')
  private serverInitialised(): void {
    this.initialiseChatBuffers();
    this.subscribeToMessageStreams();
    this.initialiseChatContextMenu();
    if (this.isCmChat()) {
      this.subscribeToBanSinPrizeEvents();
    }
  }

  @Watch('isChatFrozen')
  private isChatFrozenChanged(value: boolean): void {
    if (value) {
      this.appendFreezeEnabledMessageToActiveMessageList();
    } else {
      this.appendFrozenMessagesToActiveMessageListAndClearFrozenMessages();
    }
  }

  private initialiseChatBuffers(): void {
    const maxChatBufferSize = Number(this.configService.getConfigurationItem(ConfigurationKey.MAX_CHAT_BUFFER_SIZE));
    this.chatBuffer = new ChatBuffer(this.chatMessageList, maxChatBufferSize);
    this.frozenChatBuffer = new ChatBuffer([], maxChatBufferSize);
  }

  private disposeChatBuffers(): void {
    if (this.chatBuffer) {
      this.chatBuffer.dispose();
    }
    if (this.frozenChatBuffer) {
      this.frozenChatBuffer.dispose();
    }
  }

  private subscribeToMessageStreams(): void {
    this.serverSubscriptionStore = new ServerServiceSubscriptionStore(
      this.server.subscribeHistory((payload: HistoryResponse) => this.populateHistoryMessages(payload)),
      this.server.subscribeAddUser((payload: AddUserResponse) => this.declareUserHasJoinedRoom(payload)),
      this.server.subscribeRemoveUser((payload: RemoveUserResponse) => this.declareUserHasLeftRoom(payload)),
      this.server.subscribeMessage((payload: MessageResponse) => this.addNewMessage(payload)),
      this.server.subscribeChatPrize((payload: ChatPrizeResponse) => this.updateChatPrizeStatus(payload)),
      this.server.subscribeAuth((payload: IAuthenticationResponse) => this.requestChatHistory())
    );

    if (this.isCmChat() && !this.isCsChat()) {
      this.serverSubscriptionStore.push(
        this.server.subscribeJackpotWinnerMessage((payload: JackpotWinnerMessageResponse) => this.sendJackpotWinnerMessage(payload))
      );

      this.$eventbus.$on(ArcadeWinnerReceivedEventName, this.sendArcadeWinnerMessage);
    }
  }

  private unsubscribeFromMessageStreams() {
    if (this.serverSubscriptionStore) {
      this.serverSubscriptionStore.unsubscribeAll();
    }
    this.$eventbus.$off(ArcadeWinnerReceivedEventName, this.sendArcadeWinnerMessage);
  }

  private initialiseChatContextMenu(): void {
    const chatContextMenuFactory: IChatContextMenuFactory = this.$container.get(SERVICE_IDENTIFIERS.IChatContextMenuFactory);
    this.chatContextMenu = chatContextMenuFactory.createChatContextMenuModule(this.server);
  }

  private updateChatPrizeStatus(payload: ChatPrizeResponse): void {
    this.chatContextMenu.updateChatPrizeStatus(payload);
  }

  private async addNewMessage(message: MessageResponse): Promise<void> {
    const showTimestamp = this.$tombolaStore.Get(UserPreferences).displayTimestamps;
    message.message = this.replaceDuplicateSpaces(message.message);
    const chatMessage = await this.getChatMessageModelFromMessageResponse(message, showTimestamp);
    this.addMessageToList(chatMessage);
    this.checkMessageForAlertWords(chatMessage);
  }

  private replaceDuplicateSpaces(message: string): string {
    return message.replace(/\s(?=\s)/g, '&nbsp;');
  }

  private checkMessageForAlertWords(message: ChatMessageModel): void {
    if ((!this.isCmChat() && message.userChatStatus === UserChatStatus.StandardUser) || this.shouldShowAlertWordsInCmChat()) {
      this.alertWordService.checkAlertWords({
        message,
        gameName: this.gameName,
        chatRoomName: this.chatRoomName,
        modalGuid: this.modalGuid
      });
    }
  }

  private async getChatModsConfiguration(): Promise<IChatModsConfiguration> {
    const configurationService: IConfigurationService = container.get(SERVICE_IDENTIFIERS.IConfigurationService);
    return await configurationService.getConfiguration();
  }

  private async shouldShowAlertWordsInCmChat(): Promise<boolean> {
    const config = await this.getChatModsConfiguration();
    return config.getConfigurationByPath(ConfigurationPaths.users.shouldShowAlertWordsInCmChat) === 'true';
  }

  private async addHistoryMessage(message: MessageResponse) {
    const showTimestamp = false;
    const chatMessage = await this.getChatMessageModelFromMessageResponse(message, showTimestamp);
    this.addMessageToListIfValid(chatMessage);
  }

  private async getChatMessageModelFromMessageResponse(message: MessageResponse, showTimestamp: boolean): Promise<ChatMessageModel> {
    const roomUser = this.roomUserContainer.findRoomUser(message.username);
    const extraStylingClasses = roomUser ? roomUser.getCssClassBasedOnUserInfo() : '';
    const userChatStatus = await this.userChatStatusService.getChatStatusForUser(message.username);
    const decodedMessage = __decodeString(__decodeString(message.message));
    const messageTimestamp = showTimestamp ? this.getFormattedTimestamp() : '';
    // break down the old font tag into useable parts, retrive groups
    const data =  fontTagExtractor.extractContent(decodedMessage);

    const chatMessage = new ChatMessageModel(
      message.messageType,
      message.messageId,
      message.username,
      data.text,
      messageTimestamp,
      userChatStatus,
      extraStylingClasses,
      null,
      data.colour
    );
    chatMessage.chatPrize = message.chatPrize;
    chatMessage.newMember = message.newMember;
    return chatMessage;
  }

  private async sendJackpotWinnerMessage(message: JackpotWinnerMessageResponse) {
    if (message.mainprize > 0) {
      const isCmChat = this.isCmChat();
      const isCsChat = this.isCsChat();
      const isTeamLeader = this.isLoggedInUserTeamLeader();
      const isShiftLeader = this.isLoggedInUserShiftLeader();
      const isOnShift = this.isLoggedInUserOnShift();
      try {
        const jackpotMessage = await this.buildJackpotPrizeChatMessage(message);
        this.addMessageToList(jackpotMessage);

        if (isCmChat && !isTeamLeader && !isShiftLeader && isOnShift && !isCsChat) {
          const gameShouldBroadcast = await this.gameShouldBroadcast(message.game);
          if (gameShouldBroadcast) {
            this.emitJackpotPrizeNotificationEvent(jackpotMessage);
            const isAFreeGame = await this.isFreeGame(message.game);
            if (isAFreeGame) {
              this.emitArcadeWinnerNotificationEvent(jackpotMessage);
            }
          }
        }
      }
      catch (err) {
        this.logError(err);
      }
    }
  }

  private async sendArcadeWinnerMessage(message: ArcadeWinnerMessageResponse) {
    const userPreferences = this.$tombolaStore.Get(UserPreferences);
    this.logInfo(`sendArcadeWinnerMessage: Send Aracade Winner Function Hit. Country: ${userPreferences.country}`, message);
    if (message.prize > 0) {
      const isCmChat = this.isCmChat();
      const isCsChat = this.isCsChat();
      const isTeamLeader = this.isLoggedInUserTeamLeader();
      const isShiftLeader = this.isLoggedInUserShiftLeader();
      const isOnShift = this.isLoggedInUserOnShift();
      if (isCmChat && !isTeamLeader && !isShiftLeader && isOnShift && !isCsChat) {
        this.logInfo(`sendArcadeWinnerMessage: Chatmod Has Permission to broadcast. Country: ${userPreferences.country}`);
        try {
          const gameShouldBroadcast = await this.gameShouldBroadcast(message.game, true);
          if (gameShouldBroadcast) {
            this.logInfo(`sendArcadeWinnerMessage: Game Should Broadcast. Country: ${userPreferences.country}`);
            const winnerMessage = await this.buildArcadeWinnerChatMessage(message);
            const config = await this.getChatModsConfiguration();
            let delayInMs = Number(config.getConfigurationByPath(ConfigurationPaths.arcadegames.topPrizeDelay));
            delayInMs = isNaN(delayInMs) ? 0 : delayInMs;

            setTimeout(() => {
              this.logInfo(`sendArcadeWinnerMessage: Arcade Winner Message Broadcasted to Chat. Country: ${userPreferences.country}`, winnerMessage);
              this.emitArcadeWinnerNotificationEvent(winnerMessage);
            }, delayInMs);
          }
        }
        catch (err) {
          this.logError(err);
        }
      }
    }
  }

  private async gameShouldBroadcast(gameName: string, isArcadeTopPrize: boolean = false): Promise<boolean> {
    const userPreferences = this.$tombolaStore.Get(UserPreferences);

    // Check the FreeGame first, there are cases where CMs only want those broadcasts (broadcastWorldsJackpots = freegame flag)
    if (await this.isFreeGame(gameName)) {
      return userPreferences.broadcastWorldsJackpots;
    }

    if (isArcadeTopPrize) {
      return userPreferences.broadcastArcadeTopPrizes;
    }

    // RS - We can only get here if it's a Bingo game
    if (userPreferences.broadcastAllJackpots) {
      return true;
    }

    return false;
  }

  private isLoggedInUserTeamLeader(): boolean {
    const user = this.$tombolaStore.Get(User);
    return user.userChatStatus === UserChatStatus.TeamLeader;
  }

  private isLoggedInUserShiftLeader(): boolean {
    const user = this.$tombolaStore.Get(User);
    return user.userChatStatus === UserChatStatus.ShiftLeader;
  }

  private isLoggedInUserOnShift(): boolean {
    const userPreferences = this.$tombolaStore.Get(UserPreferences);
    return userPreferences.cmMessageColour !== CMMessageColourDefaults.BLUE;
  }

  private async getLocale(): Promise<string> {
    const configuration = await this.configurationService.getConfiguration();
    const countryDetails = configuration.getConfigurationByPath(ConfigurationPaths.regiontime.details);
    let locale = 'en-GB';

    if (countryDetails) {
      const split = countryDetails.split(',');
      if (split.length === 2) {
        const localeFromDetails = split[0];
        if (localeFromDetails) {
          return locale = localeFromDetails;
        }
      }
    }

    return locale;
  }

  private async buildJackpotPrizeChatMessage(message: JackpotWinnerMessageResponse): Promise<ChatMessageModel> {
    let jackpotAlertPrefix = this.$lang.chat.message.jackpotAlert;
    if (await this.isFreeGame(message.game)) {
      jackpotAlertPrefix = this.$lang.chat.message.freeGameWinnerAlert;
    }
    const locale = await this.getLocale();
    const totalPrizeValueInPounds = numberToLocalFormat((message.totalPrizeValueInPence / 100), locale);
    const clubName = await this.getClubNameForGameNameAndClubId(message.game, message.clubId);
    const gameName = this.gameLocalisation.getLocalGameName(this.getJackpotPrizeChatMessageGameName(message.game, clubName));
    const messageTemplate: string = this.$lang.chat.jackpot.alert;
    const messageText = messageTemplate.format({
      username: message.username,
      totalPrizeValueInPounds,
      gameName
    });
    return new ChatMessageModel(
      message.messageType,
      message.messageId,
      jackpotAlertPrefix,
      messageText,
      null,
      null,
      null,
      jackpotAlertPrefix
    );
  }

  private async buildArcadeWinnerChatMessage(message: ArcadeWinnerMessageResponse): Promise<ChatMessageModel> {
    const arcadeWinnerAlertPrefix = this.$lang.chat.message.arcadeWinnerAlert;
    const locale = await this.getLocale();
    const totalPrizeValueInPounds = numberToLocalFormat((message.prize / 100), locale);
    const gameName = this.gameLocalisation.getLocalGameName(message.game, message.skinname);
    const messageTemplate: string = this.$lang.chat.jackpot.alert;
    const messageText = messageTemplate.format({
      username: message.username,
      totalPrizeValueInPounds,
      gameName
    });
    return new ChatMessageModel(
      message.messageType,
      message.messageId,
      arcadeWinnerAlertPrefix,
      messageText,
      null,
      null,
      null,
      arcadeWinnerAlertPrefix
    );
  }

  private async getClubNameForGameNameAndClubId(gameName: string, clubId: number): Promise<string> {
    const hasSingleActiveClub = await this.gameConfigurationService.gameHasSingleActiveClub(gameName);
    if (!hasSingleActiveClub) {
      return await this.gameConfigurationService.getClubNameForGameNameAndClubId(gameName, clubId);
    }
    return null;
  }

  private async isFreeGame(gameName: string): Promise<boolean> {
    return await this.gameConfigurationService.isGameAfreeGame(gameName);
  }

  private getJackpotPrizeChatMessageGameName(gameName: string, clubName: string): string {
    return clubName && clubName !== gameName ? `${gameName} (${clubName})` : gameName;
  }

  private emitJackpotPrizeNotificationEvent(jackpotMessage: ChatMessageModel) {
    this.$eventbus.$emit(JackpotPrizeNotificationEventName, jackpotMessage);
  }

  private emitArcadeWinnerNotificationEvent(winnerMessage: ChatMessageModel) {
    this.$eventbus.$emit(ArcadeWinnerNotificationEventName, winnerMessage);
  }

  private getFormattedTimestamp(): string {
    const date = new Date();
    return `[${this.prefixSingleValueNumberWithZero(date.getHours())}:${this.prefixSingleValueNumberWithZero(date.getMinutes())}] `;
  }

  private prefixSingleValueNumberWithZero(value: number) {
    return (value < 10 ? '0' : '') + value;
  }

  private populateHistoryMessages(payload: HistoryResponse) {
    payload.messages.forEach((message) => this.addHistoryMessage(message));
    this.server.sendOfflineMessageBuffer();
  }

  private async declareUserHasJoinedRoom(message: AddUserResponse): Promise<void> {
    if (!this.showEnterAndExitMessages) {
      return;
    }

    const userChatStatus = await this.userChatStatusService.getChatStatusForUser(message.username);
    const chatMessage = new ChatMessageModel(
      message.messageType,
      message.messageId,
      message.username,
      this.$lang.chat.message.userEnteredRoom,
      this.getFormattedTimestamp(),
      userChatStatus
    );

    this.addMessageToList(chatMessage);
  }

  private async declareUserHasLeftRoom(message: RemoveUserResponse): Promise<void> {
    if (!this.showEnterAndExitMessages) {
      return;
    }

    const userChatStatus = await this.userChatStatusService.getChatStatusForUser(message.username);
    const chatMessage = new ChatMessageModel(
      message.messageType,
      message.messageId,
      message.username,
      this.$lang.chat.message.userLeftRoom,
      this.getFormattedTimestamp(),
      userChatStatus
    );

    this.addMessageToList(chatMessage);
  }

  private requestChatHistory() {
    const historyMessage = {
      toPayload() {
        return `<admin command="history" amount="20"/>`; // start="0" amount="100"
      }
    };
    this.server.send(historyMessage);
  }

  private addMessageToList(chatMessage: ChatMessageModel): void {
    const chatBuffer = this.selectChatBuffer();
    chatBuffer.push(chatMessage);
  }

  private addMessageToListIfValid(chatMessage: ChatMessageModel): void {
    const chatBuffer = this.selectChatBuffer();
    if (chatBuffer.canPushMessage(chatMessage)) {
      chatBuffer.push(chatMessage);
    }
  }

  private selectChatBuffer(): ChatBuffer {
    return this.isChatFrozen ? this.frozenChatBuffer : this.chatBuffer;
  }

  private appendFreezeEnabledMessageToActiveMessageList() {
    this.chatBuffer.push(this.getFreezeMessage());
  }

  private appendFrozenMessagesToActiveMessageListAndClearFrozenMessages() {
    this.chatBuffer.push(...this.frozenChatBuffer.toArray());
    this.chatBuffer.push(this.getFreezeMessage());
    this.frozenChatBuffer.empty();
  }

  private getFreezeMessage(): ChatMessageModel {
    const timeStamp = this.getFormattedTimestamp();
    const messageText = this.isChatFrozen ? this.$lang.chat.freeze.enabled : this.$lang.chat.freeze.disabled;
    return new ChatMessageModel(MessageType.ChatFreeze, null, null, messageText, timeStamp);
  }

  private subscribeToBanSinPrizeEvents() {
    this.banSinPrizeEventHandler = this.handleBanSinPrizeEvent.bind(this);
    this.$eventbus.$on(CMBanSinPrizeEventName, this.banSinPrizeEventHandler);
  }

  private unsubscribeFromBanSinPrizeEvents() {
    if (this.banSinPrizeEventHandler) {
      this.$eventbus.$off(CMBanSinPrizeEventName, this.banSinPrizeEventHandler);
      this.banSinPrizeEventHandler = null;
    }
  }

  private handleBanSinPrizeEvent(message: string): void {
    Vue.nextTick(() => {
      const cmPrefixPromise = this.userChatStatusService.getCMPrefix();
      cmPrefixPromise.then((cmPrefix) => {
        const MessageXML = `<FONT COLOR="#ff0000">${__encodeString(message)}</FONT>`;
        const encodedMessage = __encodeString(MessageXML);
        const cmUsername = this.$tombolaStore.Get(User).username;
        const notificationMessage = {
          toPayload() {
            return `<chat command="message" user="${cmPrefix}-${cmUsername}" msg="${encodedMessage}"></chat>`;
          }
        };
        this.server.send(notificationMessage);
      });
    });
  }

  public launchUserContextMenu(e: MouseEvent, username: string) {
    const user = this.roomUserContainer.findRoomUser(username);
    this.chatContextMenu.buildUserContextMenuConfiguration(username, this.chatRoomName, user).then((menuConfig) => {
      this.$eventbus.$emit(OpenContextMenuEventName, e, menuConfig);
    });
  }

  private getCssClassForMessageUsername(message: ChatMessageModel): string {
    let cssClass = '';
    if (message.chatPrize) cssClass += ' bling-user';
    if (message.notEligibleForPromo) cssClass += ' notEligibleForPromo-user';
    if (message.notEligibleForChatPrize) cssClass += ' notEligibleForChatPrize-user';
    if (message.newMember) cssClass += ' new-member';
    if (message.usernameStylingClasses) cssClass += ' ' + message.usernameStylingClasses;
    if (message.troubleMaker) cssClass += ' ' + ' chat-troublemaker';
    // TODO: Find additional user statuses (i.e. twice-sinned, birthday, and returning-player)
    return cssClass;
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/components/chat/_components/message-list/message-list.component.ts